import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';

// Helper function to get user-friendly error messages
const getErrorMessage = (error: any, defaultMessage: string): string => {
  // Check for backend error messages first
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Check for HTTP status codes and provide friendly messages
  if (error?.response?.status) {
    switch (error.response.status) {
      case 401:
        return 'Usuario o contraseña incorrectos';
      case 403:
        return 'No tienes permisos para acceder';
      case 404:
        return 'Servicio no disponible. Inténtalo más tarde';
      case 500:
        return 'Error del servidor. Inténtalo más tarde';
      case 503:
        return 'Servicio temporalmente no disponible';
      default:
        return defaultMessage;
    }
  }
  
  // Network errors
  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
    return 'Error de conexión. Verifica tu conexión a internet';
  }
  
  // Timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'La conexión tardó demasiado. Inténtalo de nuevo';
  }
  
  return defaultMessage;
};

// Types
export interface AdminUser {
  username: string;
  isAuthenticated: boolean;
}

export interface Reservation {
  id: number;
  room_name: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  date: string | null;
  time: string | null;
  num_people: number | null;
  total_price: number | null;
  status: 'pending' | 'paid' | 'cancelled' | 'corrupted' | null;
  created_at: string | null;
}

interface AdminState {
  user: AdminUser | null;
  reservations: Reservation[];
  loading: boolean;
  error: string | null;
  currentTimeFilter: 'all' | 'active' | 'past';
  hasInitiallyLoaded: boolean;
}

type AdminAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: AdminUser }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'LOAD_RESERVATIONS_START' }
  | { type: 'LOAD_RESERVATIONS_SUCCESS'; payload: Reservation[] }
  | { type: 'LOAD_RESERVATIONS_FAILURE'; payload: string }
  | { type: 'UPDATE_RESERVATION_STATUS'; payload: { id: number; status: string } }
  | { type: 'SET_TIME_FILTER'; payload: 'all' | 'active' | 'past' }
  | { type: 'CLEAR_ERROR' };

const initialState: AdminState = {
  user: null,
  reservations: [],
  loading: false,
  error: null,
  currentTimeFilter: 'all', // Changed to 'all' to show all reservations by default
  hasInitiallyLoaded: false,
};

function adminReducer(state: AdminState, action: AdminAction): AdminState {
  console.log('AdminReducer action:', action.type, action);
  
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return { ...state, loading: false, user: action.payload, error: null };
    case 'LOGIN_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'LOGOUT':
      return { ...state, user: null, reservations: [], hasInitiallyLoaded: false };
    case 'LOAD_RESERVATIONS_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_RESERVATIONS_SUCCESS':
      return { ...state, loading: false, reservations: action.payload, error: null, hasInitiallyLoaded: true };
    case 'LOAD_RESERVATIONS_FAILURE':
      return { ...state, loading: false, error: action.payload, reservations: [] };
    case 'UPDATE_RESERVATION_STATUS':
      return {
        ...state,
        reservations: state.reservations.map(reservation =>
          reservation.id === action.payload.id
            ? { ...reservation, status: action.payload.status as any }
            : reservation
        ),
      };
    case 'SET_TIME_FILTER':
      return { ...state, currentTimeFilter: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

interface AdminContextType {
  state: AdminState;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loadReservations: (timeFilter?: 'active' | 'past') => Promise<void>;
  setTimeFilter: (filter: 'all' | 'active' | 'past') => void;
  updateReservationStatus: (id: number, status: string) => Promise<void>;
  clearError: () => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, initialState);

  // Create stable dispatch reference
  const stableDispatch = React.useRef(dispatch);
  stableDispatch.current = dispatch;

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const username = localStorage.getItem('adminUsername');
    if (token && username) {
      stableDispatch.current({
        type: 'LOGIN_SUCCESS',
        payload: { username, isAuthenticated: true },
      });
    }
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<void> => {
    stableDispatch.current({ type: 'LOGIN_START' });
    try {
      const response = await adminApi.login(username, password);
      localStorage.setItem('authToken', response.access_token);
      localStorage.setItem('refreshToken', response.refresh_token);
      localStorage.setItem('adminUsername', username);
      
      stableDispatch.current({
        type: 'LOGIN_SUCCESS',
        payload: { username, isAuthenticated: true },
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Error de autenticación. Inténtalo de nuevo');
      stableDispatch.current({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  }, []);

  const logout = useCallback((): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('adminUsername');
    stableDispatch.current({ type: 'LOGOUT' });
  }, []);

  const loadReservations = useCallback(async (timeFilter?: 'active' | 'past'): Promise<void> => {
    console.log('loadReservations called with filter:', timeFilter);
    stableDispatch.current({ type: 'LOAD_RESERVATIONS_START' });
    try {
      const reservations = await adminApi.getReservations(timeFilter);
      console.log('loadReservations success, got', reservations.length, 'reservations');
      console.log('First few reservations:', reservations.slice(0, 3));
      stableDispatch.current({ type: 'LOAD_RESERVATIONS_SUCCESS', payload: reservations });
    } catch (error: any) {
      console.log('loadReservations error:', error);
      // If 401, logout automatically (session expired)
      if (error?.response?.status === 401) {
        // Use stable dispatch reference to avoid dependency
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('adminUsername');
        stableDispatch.current({ type: 'LOGOUT' });
        return;
      }
      const errorMessage = getErrorMessage(error, 'No se pudieron cargar las reservas');
      stableDispatch.current({ type: 'LOAD_RESERVATIONS_FAILURE', payload: errorMessage });
    }
  }, []); // No dependencies - completely stable

  const updateReservationStatus = useCallback(async (id: number, status: string): Promise<void> => {
    try {
      await adminApi.updateReservationStatus(id, status);
      stableDispatch.current({ type: 'UPDATE_RESERVATION_STATUS', payload: { id, status } });
    } catch (error: any) {
      // If 401, logout automatically (session expired)
      if (error?.response?.status === 401) {
        // Use stable dispatch reference to avoid dependency
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('adminUsername');
        stableDispatch.current({ type: 'LOGOUT' });
        throw error;
      }
      const errorMessage = getErrorMessage(error, 'No se pudo actualizar la reserva');
      stableDispatch.current({ type: 'LOAD_RESERVATIONS_FAILURE', payload: errorMessage });
      throw error;
    }
  }, []); // No dependencies - completely stable

  const setTimeFilter = useCallback((filter: 'all' | 'active' | 'past'): void => {
    console.log('Setting time filter to:', filter);
    stableDispatch.current({ type: 'SET_TIME_FILTER', payload: filter });
    
    // Automatically load reservations with new filter
    const filterParam = filter === 'all' ? undefined : filter as 'active' | 'past';
    loadReservations(filterParam);
  }, [loadReservations]);

  const clearError = useCallback((): void => {
    stableDispatch.current({ type: 'CLEAR_ERROR' });
  }, []);

  // Auto-load reservations when user logs in
  React.useEffect(() => {
    if (state.user?.isAuthenticated && !state.hasInitiallyLoaded && !state.loading) {
      console.log('Auto-loading reservations for authenticated user');
      // TEMPORARILY load ALL reservations to test if data exists
      loadReservations(undefined); // This should load all reservations
    }
  }, [state.user?.isAuthenticated, state.hasInitiallyLoaded, state.loading, loadReservations]);

  const contextValue = React.useMemo(() => ({
    state,
    login,
    logout,
    loadReservations,
    setTimeFilter,
    updateReservationStatus,
    clearError,
  }), [state, login, logout, loadReservations, setTimeFilter, updateReservationStatus]);

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};